import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { SiteMetric } from '../types';

interface SiteFilterProps {
    sites: SiteMetric[];
    selectedSites: string[];
    onToggle: (siteId: string) => void;
    onSelectAll: () => void;
    onClearAll: () => void;
}

const UPTIME_COLOR = (uptime: number): string => {
    if (uptime >= 99.9) return 'var(--status-up)';
    if (uptime >= 99) return 'var(--brand)';
    if (uptime >= 95) return 'var(--status-warn)';
    return 'var(--status-down)';
};

export const SiteFilter: React.FC<SiteFilterProps> = ({
    sites,
    selectedSites,
    onToggle,
    onSelectAll,
    onClearAll,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const allSelected = selectedSites.length === 0;

    const label = allSelected
        ? 'Все сайты'
        : selectedSites.length === 1
        ? (sites.find(s => s.siteId === selectedSites[0])?.siteName ?? '1 сайт')
        : `${selectedSites.length} сайта`;

    useEffect(() => {
        const onClick = (e: MouseEvent) => {
        if (ref.current && !ref.current.contains(e.target as Node)) {
            setIsOpen(false);
        }
        };
        const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        document.addEventListener('keydown', onKey);
        return () => {
        document.removeEventListener('mousedown', onClick);
        document.removeEventListener('keydown', onKey);
        };
    }, []);

    return (
        <div className="relative" ref={ref}>
            <motion.button
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                onClick={() => setIsOpen(v => !v)}
                aria-expanded={isOpen}
                aria-haspopup="listbox"
                className={`
                    flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[12px] font-medium transition-all
                    ${isOpen
                        ? 'bg-[var(--brand-dim)] border-[var(--brand-border)] text-[var(--brand)]'
                        : 'bg-[var(--bg-elevated)] border-[var(--border)] text-[var(--text-secondary)] hover:border-[var(--border-strong)] hover:text-[var(--text)]'
                    }
                `}
            >
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                </svg>

                <span>{label}</span>

                {!allSelected && (
                    <span className="bg-[var(--brand)] text-white rounded-full text-[9px] font-semibold px-1.5 py-0.5 leading-none">
                        {selectedSites.length}
                    </span>
                )}

                <motion.svg
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                    width="11"
                    height="11"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                >
                    <polyline points="6 9 12 15 18 9"/>
                </motion.svg>
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 5, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 5, scale: 0.97 }}
                        transition={{ duration: 0.14 }}
                        className="absolute top-full left-0 mt-1.5 bg-[var(--bg-elevated)] border border-[var(--border-strong)] rounded-xl shadow-lg z-50 overflow-hidden"
                        style={{ minWidth: 228 }}
                        role="listbox"
                        aria-multiselectable="true"
                    >
                        <div className="flex items-center justify-between px-3 py-2.5 border-b border-[var(--border)]">
                            <span className="text-[11px] font-medium text-[var(--text-secondary)]">
                                Сайты <span className="text-[var(--text-muted)]">({sites.length})</span>
                            </span>
                            <div className="flex gap-2 items-center">
                                {!allSelected && (
                                    <button
                                        onClick={() => { onClearAll(); }}
                                        className="text-[11px] text-[var(--text-muted)] hover:text-[var(--text)] transition-colors"
                                    >
                                        Сбросить
                                    </button>
                                )}
                                {!allSelected && <span className="text-[var(--border)] text-xs">·</span>}
                                <button
                                    onClick={() => { onSelectAll(); setIsOpen(false); }}
                                    className="text-[11px] text-[var(--brand)] hover:opacity-75 transition-opacity font-medium"
                                >
                                    Все
                                </button>
                            </div>
                        </div>

                        <div className="max-h-[280px] overflow-y-auto overscroll-contain">
                            {sites.map(site => {
                                const isSelected = selectedSites.includes(site.siteId);
                                const dotColor = UPTIME_COLOR(site.uptime);

                                return (
                                    <motion.button
                                        key={site.siteId}
                                        whileHover={{ backgroundColor: 'var(--bg-hover)' }}
                                        onClick={() => onToggle(site.siteId)}
                                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors"
                                        role="option"
                                        aria-selected={isSelected}
                                    >
                                        <span
                                            className={`
                                                w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center
                                                transition-all duration-150
                                                ${isSelected
                                                    ? 'bg-[var(--brand)] border-[var(--brand)]'
                                                    : 'bg-transparent border-[var(--border-strong)]'
                                                }
                                            `}
                                        >
                                            {isSelected && (
                                                <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
                                                    <polyline
                                                        points="1.5,3.5 3.5,5.5 7.5,1.5"
                                                        stroke="white"
                                                        strokeWidth="1.5"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            )}
                                        </span>

                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />

                                        <div className="flex-1 min-w-0">
                                            <div className="text-[12px] font-medium text-[var(--text)] truncate">
                                                {site.siteName}
                                            </div>
                                            <div className="text-[10px] text-[var(--text-muted)] flex gap-2">
                                                <span>{site.uptime.toFixed(2)}%</span>
                                                <span>·</span>
                                                <span>{site.avgResponseTime}мс</span>
                                            </div>
                                        </div>

                                        {site.incidents > 0 && (
                                            <span className="flex-shrink-0 text-[10px] font-semibold text-[var(--status-down-text)] bg-[var(--status-down-bg)] px-1.5 py-0.5 rounded-full">
                                                {site.incidents}
                                            </span>
                                        )}
                                    </motion.button>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};